import { TUNABLES } from "../config";
import { type EventBatch, type RecordedEvent, recordedFrom } from "../net/events";
import type { TelemetryEvent } from "./bus";

/**
 * The page's events on their way to the server (B3.9): the telemetry events the playtest records
 * (`RECORDED_TYPES`) are kept in a batch and sent when it holds `maxEvents`, every `everyMs` while
 * it holds any, and when the page is hidden or closed (`flush`). The send is the page's
 * `navigator.sendBeacon`, which outlives the page's close; a test gives a fake. A batch the browser
 * would not queue is dropped, never retried: the playtest's numbers are worth less than a page
 * that waits.
 */
export interface Recorder {
  /** One of the game's telemetry events; those the playtest does not record are dropped here. */
  record(event: TelemetryEvent): void;
  /** The duel's code or the run's seed text that the next events belong to. */
  setGame(game: string | undefined): void;
  /** Sends what is held now. */
  flush(): void;
  /** Batches sent and events sent, for the debug hook. */
  sent(): { readonly batches: number; readonly events: number };
}

export function createRecorder(options: {
  readonly url: string;
  readonly guest: string;
  readonly session: string;
  readonly build: string;
  /** Milliseconds since the page loaded. */
  readonly now: () => number;
  /** Queues a body for the URL; false when the browser would not. */
  readonly send: (url: string, body: string) => boolean;
  /** Runs `task` every `ms`. */
  readonly every: (ms: number, task: () => void) => void;
  /** AN1: sees every event offered, recorded or not (the page's visit, `visit.ts`). */
  readonly observe?: (event: TelemetryEvent) => void;
}): Recorder {
  let held: RecordedEvent[] = [];
  let game: string | undefined;
  let batches = 0;
  let events = 0;

  const flush = () => {
    if (held.length === 0) {
      return;
    }
    const batch: EventBatch = {
      guest: options.guest,
      session: options.session,
      build: options.build,
      events: held,
    };
    held = [];
    if (options.send(options.url, JSON.stringify(batch))) {
      batches += 1;
      events += batch.events.length;
    }
  };

  options.every(TUNABLES.events.everyMs, flush);
  return {
    record(event) {
      options.observe?.(event);
      const recorded = recordedFrom(event, options.now(), game);
      if (!recorded) {
        return;
      }
      held.push(recorded);
      if (held.length >= TUNABLES.events.maxEvents) {
        flush();
      }
    },
    setGame(next) {
      game = next;
    },
    flush,
    sent: () => ({ batches, events }),
  };
}
